import {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import { Paper, Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useDispatch, useSelector } from 'react-redux';

import DeviceList from './DeviceList';
import BottomMenu from '../common/components/BottomMenu';
import StatusCard from '../common/components/StatusCard';
import MobileDeviceBar from '../common/components/MobileDeviceBar';
import { devicesActions } from '../store';
import usePersistedState from '../common/util/usePersistedState';
import EventsDrawer from './EventsDrawer';
import useFilter from './useFilter';
import MainToolbar from './MainToolbar';
import MainMap from './MainMap';
import { useAttributePreference } from '../common/util/preferences';

const LIST_WIDTH = 340;
const DETAILS_WIDTH = 440;
const PAGE_GAP = 12;
const MAP_FOCUS_ZOOM = 18;

const MOBILE_CARD_HEIGHT = 640;
const MOBILE_CARD_MEDIUM_HEIGHT = 340;
const MOBILE_LIST_WIDTH = '100vw';
const TABLET_LIST_WIDTH = 'min(360px, 88vw)';
const MOBILE_BAR_ESTIMATED_HEIGHT = 116;

const useStyles = makeStyles()((theme) => ({
  root: {
    height: '100%',
    width: '100%',
    overflow: 'hidden',
    background: theme.palette.background.default,
  },

  desktopRoot: {
    height: '100%',
    width: '100%',
    display: 'grid',
    gridTemplateColumns: `${LIST_WIDTH}px minmax(${DETAILS_WIDTH}px, ${DETAILS_WIDTH}px) minmax(0, 1fr)`,
    gap: PAGE_GAP,
    padding: theme.spacing(1.25),
    boxSizing: 'border-box',
  },

  desktopRootNoDetails: {
    gridTemplateColumns: `${LIST_WIDTH}px minmax(0, 1fr)`,
  },

  leftColumn: {
    minHeight: 0,
    display: 'grid',
    gridTemplateRows: 'auto 1fr auto',
    gap: theme.spacing(1),
    overflow: 'hidden',
  },

  header: {
    minHeight: 0,
    overflow: 'hidden',
    borderRadius: theme.shape.borderRadius,
  },

  listPaper: {
    minHeight: 0,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: theme.shape.borderRadius,
  },

  detailsColumn: {
    minHeight: 0,
    minWidth: 0,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },

  detailsPaper: {
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    overflow: 'hidden',
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    flexDirection: 'column',
  },

  mapColumn: {
    minWidth: 0,
    minHeight: 0,
    overflow: 'hidden',
    borderRadius: theme.shape.borderRadius,
    position: 'relative',
    background: theme.palette.background.paper,
  },

  footer: {
    minHeight: 0,
  },

  mobileRoot: {
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    position: 'relative',
  },

  mobileHeader: {
    flexShrink: 0,
    zIndex: 7,
  },

  middle: {
    flex: 1,
    display: 'grid',
    minHeight: 0,
    position: 'relative',
    overflow: 'hidden',
  },

  contentMap: {
    pointerEvents: 'auto',
    gridArea: '1 / 1',
    minHeight: 0,
    zIndex: 1,
  },

  contentList: {
    pointerEvents: 'auto',
    gridArea: '1 / 1',
    zIndex: 4,
    display: 'flex',
    minHeight: 0,
    overflow: 'hidden',
    width: MOBILE_LIST_WIDTH,
    maxWidth: MOBILE_LIST_WIDTH,
    height: '100%',
    borderRadius: 0,
    background: theme.palette.background.paper,
    transition: 'transform 0.2s ease, opacity 0.2s ease, visibility 0.2s ease',
    [theme.breakpoints.up('sm')]: {
      width: TABLET_LIST_WIDTH,
      maxWidth: TABLET_LIST_WIDTH,
      borderTopRightRadius: theme.shape.borderRadius * 1.5,
      borderBottomRightRadius: theme.shape.borderRadius * 1.5,
    },
  },

  hiddenList: {
    visibility: 'hidden',
    pointerEvents: 'none',
    transform: 'translateX(-8px)',
    opacity: 0,
  },

  mobileSelectionOverlay: {
    position: 'absolute',
    left: theme.spacing(1.5),
    right: theme.spacing(1.5),
    bottom: theme.spacing(1.5),
    zIndex: 6,
    pointerEvents: 'none',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-end',
  },

  mobileCollapsedBar: {
    pointerEvents: 'auto',
    width: '100%',
    maxWidth: '100%',
    minHeight: 72,
    overflow: 'hidden',
    borderRadius: theme.shape.borderRadius * 1.5,
    boxShadow: theme.shadows[8],
    background: theme.palette.background.paper,
  },

  mobileSelectionPaper: {
    pointerEvents: 'auto',
    width: '100%',
    maxWidth: '100%',
    maxHeight: '82dvh',
    height: `min(82dvh, ${MOBILE_CARD_HEIGHT}px)`,
    minHeight: 320,
    overflow: 'hidden',
    borderRadius: theme.shape.borderRadius * 1.5,
    boxShadow: theme.shadows[8],
    background: theme.palette.background.paper,
    display: 'flex',
    flexDirection: 'column',
  },
}));

const MainPage = () => {
  const { classes, cx } = useStyles();
  const dispatch = useDispatch();
  const theme = useTheme();

  const desktop = useMediaQuery(theme.breakpoints.up('md'));
  const mapOnSelect = useAttributePreference('mapOnSelect', true);

  const selectedDeviceId = useSelector((state) => state.devices.selectedId);
  const devices = useSelector((state) => state.devices.items);
  const positions = useSelector((state) => state.session.positions);

  const [filteredPositions, setFilteredPositions] = useState([]);
  const [filteredDevices, setFilteredDevices] = useState([]);

  const [keyword, setKeyword] = useState('');
  const [filter, setFilter] = usePersistedState('filter', {
    statuses: [],
    groups: [],
  });
  const [filterSort, setFilterSort] = usePersistedState('filterSort', '');
  const [filterMap, setFilterMap] = usePersistedState('filterMap', false);
  const [recentDeviceIds, setRecentDeviceIds] = usePersistedState('recentDeviceIds', []);

  const [devicesOpen, setDevicesOpen] = useState(desktop);
  const [eventsOpen, setEventsOpen] = useState(false);
  const [mobileSheetMode, setMobileSheetMode] = useState('compact');

  const mapApiRef = useRef(null);

  useFilter(
    keyword,
    filter,
    filterSort,
    filterMap,
    positions,
    setFilteredDevices,
    setFilteredPositions,
  );

  const selectedPosition = useMemo(
    () => (
      filteredPositions.find(
        (position) => position.deviceId === selectedDeviceId,
      ) ?? positions?.[selectedDeviceId] ?? null
    ),
    [filteredPositions, selectedDeviceId, positions],
  );

  const hasSelection = Boolean(selectedDeviceId);

  const focusDeviceOnMap = useCallback((deviceId, zoom = MAP_FOCUS_ZOOM) => {
    const pos = positions?.[deviceId];

    if (pos?.latitude == null || pos?.longitude == null) {
      return;
    }

    mapApiRef.current?.focusPosition(pos.latitude, pos.longitude, zoom);
  }, [positions]);

  const pushRecentDevice = useCallback((deviceId) => {
    if (!deviceId) return;

    setRecentDeviceIds((prev) => {
      const safePrev = Array.isArray(prev) ? prev : [];
      return [deviceId, ...safePrev.filter((id) => id !== deviceId)].slice(0, 10);
    });
  }, [setRecentDeviceIds]);

  const recentDevices = useMemo(() => {
    if (!Array.isArray(recentDeviceIds) || !recentDeviceIds.length) {
      return [];
    }

    const allDevices = Object.values(devices || {}).filter(Boolean);
    const deviceMap = new Map(allDevices.map((device) => [device.id, device]));

    return recentDeviceIds
      .map((id) => deviceMap.get(id))
      .filter(Boolean)
      .slice(0, 10);
  }, [recentDeviceIds, devices]);

  const onEventsClick = useCallback(() => {
    setEventsOpen(true);
  }, []);

  useEffect(() => {
    setDevicesOpen(desktop);
  }, [desktop]);

  useEffect(() => {
    if (desktop) {
      setMobileSheetMode('compact');
    }
  }, [desktop]);

  useEffect(() => {
    if (!desktop && mapOnSelect && selectedDeviceId) {
      setDevicesOpen(false);
    }
  }, [desktop, mapOnSelect, selectedDeviceId]);

  const handleSelectDevice = useCallback((deviceId, options = {}) => {
    const source = options?.source || 'list';

    dispatch(devicesActions.selectId(deviceId));
    pushRecentDevice(deviceId);

    if (!desktop) {
      setDevicesOpen(false);

      // No mobile, sempre abre minimizado primeiro.
      // Depois o usuário escolhe expandir.
      setMobileSheetMode('compact');

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          focusDeviceOnMap(deviceId, MAP_FOCUS_ZOOM);
        });
      });

      return;
    }

    // Desktop: sempre dentro da página, sem popup flutuante nesse fluxo
    focusDeviceOnMap(deviceId, source === 'map' ? MAP_FOCUS_ZOOM : MAP_FOCUS_ZOOM);
  }, [dispatch, pushRecentDevice, focusDeviceOnMap, desktop]);

  const handleLocateOnMap = useCallback((pos) => {
    if (pos?.latitude == null || pos?.longitude == null) return;

    mapApiRef.current?.focusPosition(
      pos.latitude,
      pos.longitude,
      MAP_FOCUS_ZOOM,
    );
  }, []);

  const handleCloseStatusCard = useCallback(() => {
    dispatch(devicesActions.clearSelection());
    setMobileSheetMode('compact');
  }, [dispatch]);

  const handleExpandMobileCard = useCallback(() => {
    setMobileSheetMode('expanded');

    if (selectedDeviceId) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          focusDeviceOnMap(selectedDeviceId, MAP_FOCUS_ZOOM);
        });
      });
    }
  }, [selectedDeviceId, focusDeviceOnMap]);

  const handleCollapseMobileCard = useCallback(() => {
    setMobileSheetMode('compact');

    if (selectedDeviceId) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          focusDeviceOnMap(selectedDeviceId, MAP_FOCUS_ZOOM);
        });
      });
    }
  }, [selectedDeviceId, focusDeviceOnMap]);

  const handleCloseEventsDrawer = useCallback(() => {
    setEventsOpen(false);
  }, []);

  const handleSearchFocus = useCallback(() => {
    if (!desktop) {
      setDevicesOpen(true);
      setMobileSheetMode('compact');
    }
  }, [desktop]);

  const toolbarProps = {
    filteredDevices,
    recentDevices,
    devicesOpen: desktop ? true : devicesOpen,
    setDevicesOpen,
    keyword,
    setKeyword,
    filter,
    setFilter,
    filterSort,
    setFilterSort,
    filterMap,
    setFilterMap,
    positions,
    onSearchFocus: handleSearchFocus,
    onSelectDevice: handleSelectDevice,
  };

  const mobileCardExpanded = mobileSheetMode === 'expanded';
  const mobileCardMedium = mobileSheetMode === 'medium';

  const mapProps = {
    ref: mapApiRef,
    filteredPositions,
    selectedPosition,
    onEventsClick,
    devicesOpen: desktop ? true : devicesOpen,
    hasSelection,
    mobileCardExpanded: mobileCardExpanded || mobileCardMedium,
    onSelectDevice: handleSelectDevice,
  };

  const statusCardProps = {
    deviceId: selectedDeviceId,
    position: selectedPosition,
    onLocate: handleLocateOnMap,
    onClose: handleCloseStatusCard,
    onCollapse: handleCollapseMobileCard,
    onExpand: handleExpandMobileCard,
    docked: true,
    desktopPadding: 0,
    mobileExpanded: mobileCardExpanded,
  };

  const mobileListBottomInset = !desktop && hasSelection
    ? (mobileCardExpanded ? MOBILE_CARD_HEIGHT + 28 : mobileCardMedium ? MOBILE_CARD_MEDIUM_HEIGHT + 24 : MOBILE_BAR_ESTIMATED_HEIGHT)
    : 12;

  if (desktop) {
    return (
      <div className={classes.root}>
        <div
          className={cx(
            classes.desktopRoot,
            !hasSelection && classes.desktopRootNoDetails,
          )}
        >
          <div className={classes.leftColumn}>
            <Paper elevation={3} className={classes.header}>
              <MainToolbar {...toolbarProps} />
            </Paper>

            <Paper elevation={3} className={classes.listPaper}>
              <DeviceList
                devices={filteredDevices}
                positions={positions}
                onSelect={handleSelectDevice}
              />
            </Paper>

            <div className={classes.footer}>
              <BottomMenu />
            </div>
          </div>

          {hasSelection && (
            <div className={classes.detailsColumn}>
              <Paper elevation={3} className={classes.detailsPaper}>
                <StatusCard {...statusCardProps} onCollapse={handleCollapseMobileCard} mobileExpanded={mobileCardExpanded} />
              </Paper>
            </div>
          )}

          <div className={classes.mapColumn}>
            <MainMap {...mapProps} />
          </div>
        </div>

        <EventsDrawer
          open={eventsOpen}
          onClose={handleCloseEventsDrawer}
        />
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.mobileRoot}>
        <Paper square elevation={3} className={classes.mobileHeader}>
          <MainToolbar {...toolbarProps} />
        </Paper>

        <div className={classes.middle}>
          <div className={classes.contentMap}>
            <MainMap {...mapProps} />
          </div>

          <Paper
            square
            elevation={3}
            className={cx(
              classes.contentList,
              !devicesOpen && classes.hiddenList,
            )}
          >
            <DeviceList
              devices={filteredDevices}
              positions={positions}
              onSelect={handleSelectDevice}
              bottomInset={devicesOpen ? mobileListBottomInset : 12}
            />
          </Paper>

          {hasSelection && mobileSheetMode === 'compact' && (
            <Box className={classes.mobileSelectionOverlay}>
              <Paper elevation={6} className={classes.mobileCollapsedBar}>
                <MobileDeviceBar
                  deviceId={selectedDeviceId}
                  position={selectedPosition}
                  onExpand={handleExpandMobileCard}
                  onClose={handleCloseStatusCard}
                />
              </Paper>
            </Box>
          )}

          {hasSelection && (mobileCardMedium || mobileCardExpanded) && (
            <Box className={classes.mobileSelectionOverlay}>
              <Paper
                elevation={6}
                className={classes.mobileSelectionPaper}
                style={{ height: mobileCardMedium ? 'min(58dvh, 420px)' : `min(82dvh, ${MOBILE_CARD_HEIGHT}px)` }}
              >
                <StatusCard {...statusCardProps} onCollapse={handleCollapseMobileCard} mobileExpanded={mobileCardExpanded} />
              </Paper>
            </Box>
          )}
        </div>
      </div>

      <EventsDrawer
        open={eventsOpen}
        onClose={handleCloseEventsDrawer}
      />
    </div>
  );
};

export default MainPage;
